import { useState, useEffect } from "react";
import styles from "./Airlines.module.css";
import { Search, Download, RefreshCw, Trash2, ImageDown, Undo2 } from "lucide-react";
import AddAirlineModal from "../../../../components/Products/Flights/Airlines/AddAirlineModal";
import DeleteConfirmModal from "../../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import BaseModal from "../../../../components/BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../../../context/ToastContext";
import {
  useGetAirlines,
  useCreateAirline,
  useUpdateAirline,
  useDeleteAirline,
  useSyncAirlineLogos,
  useRestoreAirlineLogos,
} from "../../../../api/products/useAirlinesApi";

const Airlines = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAirline, setEditingAirline] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, airline: null, loading: false });
  const [logoModal, setLogoModal] = useState({ open: false, mode: "missing" });
  const [syncProgress, setSyncProgress] = useState(null);

  // Debounce search to avoid API call on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: airlines = [], pagination, loading, error, refetch } = useGetAirlines({
    page: 1,
    limit: 500,
    search: debouncedSearch,
    status: statusFilter,
  });
  const { execute: createAirline } = useCreateAirline();
  const { execute: updateAirline } = useUpdateAirline();
  const { execute: deleteAirline, loading: deleteLoading } = useDeleteAirline();
  const { execute: syncLogos, loading: syncLoading } = useSyncAirlineLogos();
  const { execute: restoreLogos, loading: restoreLoading } = useRestoreAirlineLogos();

  const missingLogoCount = airlines.filter((a) => !a.logoUrl).length;
  const hasRestorableLogos = airlines.some((a) => a.logoSource && a.logoSource !== "manual");

  const handleEdit = (airline) => {
    setEditingAirline(airline);
    setShowAddModal(true);
  };

  const handleDelete = (airline) => {
    setDeleteModal({ open: true, airline });
  };

  const confirmDelete = async () => {
    if (!deleteModal.airline) return;
    
    setDeleteModal({ ...deleteModal, loading: true });
    const result = await deleteAirline(deleteModal.airline.id);
    setDeleteModal({ ...deleteModal, loading: false });
    if (result.success) {
      showToast("Airline removed successfully", "success");
      refetch();
      setDeleteModal({ open: false, airline: null });
    } else {
      showToast(result.message || "Failed to remove airline", "error");
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, airline: null });
  };

  const handleSaveAirline = async (airlineData) => {
    if (editingAirline) {
      const result = await updateAirline(editingAirline.id, airlineData);
      if (result.success) {
        showToast("Airline updated successfully", "success");
        refetch();
        handleCloseModal();
        return;
      }
      showToast(result.message || "Failed to update airline", "error");
      return result.message;
    }
    const result = await createAirline(airlineData);
    if (result.success) {
      showToast("Airline created successfully", "success");
      refetch();
      handleCloseModal();
      return;
    }
    showToast(result.message || "Failed to create airline", "error");
    return result.message;
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAirline(null);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = () => {
    console.log("EXPORT AIRLINES DATA");
    // Export functionality
  };

  const handleSyncLogos = async () => {
    setSyncProgress({ updated: 0, notFound: 0, failed: 0 });
    const result = await syncLogos({
      mode: logoModal.mode,
      onProgress: (p) => setSyncProgress(p),
    });
    setSyncProgress(null);
    setLogoModal({ open: false, mode: "missing" });

    if (!result.success) {
      showToast(result.message || "Failed to download airline logos", "error");
      return;
    }

    const { updated, notFound, failed } = result.totals;
    if (!updated && !notFound && !failed) {
      showToast("All airline logos are already in place", "success");
    } else {
      const detail = [
        notFound ? `${notFound} without artwork` : null,
        failed ? `${failed} failed` : null,
      ]
        .filter(Boolean)
        .join(", ");
      showToast(
        `${updated} airline logo${updated === 1 ? "" : "s"} downloaded${detail ? ` — ${detail}` : ""}`,
        failed ? "error" : "success"
      );
    }
    refetch();
  };

  const handleRestoreLogos = async () => {
    const result = await restoreLogos();
    if (result.success) {
      showToast(result.message || "Original logos restored", "success");
      setLogoModal({ open: false, mode: "missing" });
      refetch();
      return;
    }
    showToast(result.message || "Failed to restore original logos", "error");
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Airlines</h1>
          <p className={styles.subtitle}>Manage airline master data used across flight bookings and operations</p>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.secondaryBtn}
            onClick={() => setLogoModal({ open: true, mode: "missing" })}
            disabled={syncLoading || restoreLoading}
          >
            <ImageDown size={16} />
            {syncLoading ? "Downloading…" : "Get Logos"}
          </button>
          <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
            + Add Airline
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Master Data Notice:</strong> Airline data is synced via API and used across flight search, bookings, reporting, and schedule updates. No pricing or fare rules are stored at airline level.
        </div>
      </div>

      {/* Filters & Actions */}
      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, IATA code, ICAO code, or country..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>

          <button className={styles.iconBtn} onClick={handleExport} title="Export">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.infoBanner} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.infoIcon}>⚠️</div>
          <div className={styles.infoText}>
            {error}
            <button className={styles.retryBtn} onClick={() => refetch()}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className={styles.resultsCount}>
        Showing {airlines.length} of {pagination?.total ?? airlines.length} airlines
      </div>

      {/* Airlines Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>LOGO</th>
              <th>AIRLINE NAME</th>
              <th>IATA</th>
              <th>ICAO CODE</th>
              <th>COUNTRY</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Loading airlines…</p>
                  </div>
                </td>
              </tr>
            ) : airlines.length === 0 ? (
              <tr>
                <td colSpan="7" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>✈️</div>
                    <p>No airlines found</p>
                    <span>Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              airlines.map(airline => (
                <tr key={airline.id}>
                  <td style={{ width: 52, textAlign: "center" }}>
                    {airline.logoUrl ? (
                      <img
                        src={airline.logoUrl}
                        alt={airline.iataCode}
                        style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 6, background: "#f8f9fa" }}
                      />
                    ) : (
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 32, height: 32, borderRadius: 6, background: "#eef3ff", color: "#3b5bdb",
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                      }}>
                        {airline.iataCode || "—"}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className={styles.airlineName}>
                      <strong>{airline.airlineName}</strong>
                    </div>
                  </td>
                  <td>
                    <span className={styles.codeTag}>{airline.iataCode}</span>
                  </td>
                  <td>
                    {airline.icaoCode ? (
                      <span className={styles.codeTag}>{airline.icaoCode}</span>
                    ) : (
                      <span className={styles.emptyValue}>—</span>
                    )}
                  </td>
                  <td>{airline.country}</td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        airline.status === "active"
                          ? styles.statusActive
                          : styles.statusInactive
                      }`}
                    >
                      {airline.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button onClick={() => handleEdit(airline)}>Edit</button>
                      <button 
                        onClick={() => handleDelete(airline)}
                        className={styles.deleteBtn}
                        title="Delete airline"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AddAirlineModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSaveAirline}
        airline={editingAirline}
      />

      <BaseModal
        open={logoModal.open}
        onClose={() => !syncLoading && setLogoModal({ open: false, mode: "missing" })}
        title="Get Airline Logos"
        width="520px"
        footer={
          <div className={styles.modalFooter}>
            {hasRestorableLogos && (
              <button
                type="button"
                className={styles.restoreBtn}
                onClick={handleRestoreLogos}
                disabled={syncLoading || restoreLoading}
              >
                <Undo2 size={15} />
                {restoreLoading ? "Restoring…" : "Undo & restore originals"}
              </button>
            )}
            <div className={styles.modalFooterRight}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={() => setLogoModal({ open: false, mode: "missing" })}
                disabled={syncLoading || restoreLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.modalConfirm}
                onClick={handleSyncLogos}
                disabled={syncLoading || restoreLoading}
              >
                {syncLoading ? "Downloading…" : "Download Logos"}
              </button>
            </div>
          </div>
        }
      >
        <p className={styles.modalText}>
          Logos are downloaded once from public airline logo CDNs and stored on our own
          servers — nothing is loaded from a third party when the page renders.
        </p>

        <div className={styles.modeOptions}>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="logoMode"
              checked={logoModal.mode === "missing"}
              onChange={() => setLogoModal({ ...logoModal, mode: "missing" })}
              disabled={syncLoading}
            />
            <span>
              <strong>Only airlines without a logo</strong>
              <em>
                {missingLogoCount} of {airlines.length} shown have no logo. Existing logos
                are left untouched.
              </em>
            </span>
          </label>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="logoMode"
              checked={logoModal.mode === "all"}
              onChange={() => setLogoModal({ ...logoModal, mode: "all" })}
              disabled={syncLoading}
            />
            <span>
              <strong>All airlines — replace uploaded logos too</strong>
              <em>
                Every current logo is backed up first, so this can be undone from this
                same window.
              </em>
            </span>
          </label>
        </div>

        {syncProgress && (
          <div className={styles.progressBox}>
            Downloaded {syncProgress.updated} logo
            {syncProgress.updated === 1 ? "" : "s"}
            {syncProgress.notFound ? ` · ${syncProgress.notFound} without artwork` : ""}
            {syncProgress.remaining ? ` · ${syncProgress.remaining} to go` : ""}
          </div>
        )}
      </BaseModal>

      <DeleteConfirmModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        loading={deleteModal.loading || deleteLoading}
        title="Remove Airline"
        message={`Are you sure you want to remove "${deleteModal.airline?.airlineName}"? This action cannot be undone and will hide airline from future selections. Existing bookings will remain unchanged.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Airlines;